import type { PlatformApiClient } from "../api";
import type { OrganizationContext } from "../types";
import { SalonsProduct } from "../salons/SalonsProduct";
import { demoModuleLaunchPath, type DemoDefinition, type DemoModuleKey } from "./definitions";

const SALON_REAL_MODULES = new Set<DemoModuleKey>([
  "home",
  "customers",
  "conversations",
  "agenda",
  "sales",
  "settings",
  "services",
  "knowledge",
  "agent",
  "reports",
]);

function demoBasePath(demoId: string): string {
  return `/demos/${encodeURIComponent(demoId)}`;
}

function salonsNestedPath(moduleKey: DemoModuleKey, nestedPath: string): string {
  if (moduleKey === "home") return "/home";
  if (moduleKey === "services") return "/settings/services";
  if (moduleKey === "knowledge") return "/settings/knowledge";
  if (moduleKey === "agent") return "/settings/assistant";
  if (moduleKey === "settings") return `/settings${nestedPath}`;
  return `/${moduleKey}${nestedPath}`;
}

export function DemoModuleRuntime({
  definition,
  moduleKey,
  nestedPath,
  organizations,
  api,
  navigate,
  onExpired,
}: {
  definition: DemoDefinition;
  moduleKey: DemoModuleKey;
  nestedPath: string;
  organizations: readonly OrganizationContext[];
  api: PlatformApiClient;
  navigate: (path: string) => void;
  onExpired: () => void;
}) {
  const launch = definition.manifest.launch;

  if (
    launch.kind === "organization-salons-reference" &&
    SALON_REAL_MODULES.has(moduleKey)
  ) {
    const organization = organizations.find(
      (item) => item.organization.slug === launch.organizationSlug,
    );

    if (!organization) {
      return <section className="demo-runtime-module-card">
        <p className="eyebrow">Runtime real</p>
        <h3>{definition.manifest.name}</h3>
        <p>
          La demo está configurada para usar la organización <strong>{launch.organizationSlug}</strong>,
          pero esta sesión todavía no tiene acceso a ese espacio.
        </p>
        <p className="muted">
          El módulo ya está conectado al runtime canónico; sólo falta habilitar la membresía local del Demo Admin.
        </p>
      </section>;
    }

    const basePath = demoBasePath(definition.manifest.id);

    return <div className="demo-embedded-runtime cd-salons-shell">
      <SalonsProduct
        organization={organization}
        nestedPath={salonsNestedPath(moduleKey, nestedPath)}
        api={api}
        navigate={navigate}
        onExpired={onExpired}
        basePath={basePath}
      />
    </div>;
  }

  const selected = definition.modules.find((module) => module.key === moduleKey)!;
  const compatibleOrganizations = launch.kind === "organization-gastro-alias"
    ? organizations.filter((item) => item.products.some((p) => p.manifest.key === "menu" && p.access.allowed))
    : organizations;
  const runtimePath = demoModuleLaunchPath(definition, moduleKey, compatibleOrganizations.map((item) => item.organization.slug));
  const unavailable = launch.kind === "unavailable";

  return <section className="demo-runtime-module-card">
    <p className="eyebrow">{unavailable ? "Demo en preparación" : "Experiencia operativa"}</p>
    <h1>{definition.manifest.name}</h1>
    <h2>{selected.label}</h2>
    <p>{selected.description}</p>
    {runtimePath ? <>
      <p>Esta solución cuenta con una aplicación funcional propia. Abrila para operar
        sus módulos en el sistema de origen.</p>
      <button type="button" className="primary-button" onClick={() => navigate(runtimePath)}>
        Abrir aplicación funcional
      </button>
      <p className="muted">La aplicación se abre en su ruta canónica, sin simular
        acciones que este catálogo todavía no integra.</p>
    </> : <>
      <p role="status">
        {unavailable
          ? "Esta referencia todavía no tiene una implementación disponible en esta rama."
          : "No hay una organización habilitada para ejecutar esta referencia en tu sesión."}
      </p>
      {unavailable && launch.reason && <p className="muted">{launch.reason}</p>}
      <p className="muted">No se muestran datos ni acciones ficticias como si fueran operativos.</p>
    </>}
  </section>;
}
